import React, { useEffect, useState } from "react";
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons";
import { Alert, Col, Row, Upload } from "antd";
import { maxFileFlatRate, permittedFlatRateUploadFormats } from "../../../utils/properties";
import { get, indexOf, isEmpty, last, split, toLower, toUpper } from "lodash";
import { exportFile, showError, showSuccess } from "../../../utils/utilities";
import { useTranslation } from "react-i18next";
import Button from "../../../components/functional_components/Button/PFButton";
import ExpenseRefund from "../../../services/api/ExpenseRefund";

const { Dragger } = Upload;

function UploadFile({ fileList, setFileList, uploading, modal, canEdit }) {
  const [isDownloadButtonActive, setDownloadButtonActive] = useState({ receipt: false, payment_tracking: false });
  const [downloadLoading, setDownloadLoading] = useState({ receipt: false, payment_tracking: false });

  useEffect(() => {
    // Check if there are documents in modal.data.documents
    let checkReceipt = false;
    let checkPaymentTracking = false;
    if (modal.data?.documents?.find((doc) => doc.type === toUpper("receipt"))) {
      checkReceipt = true;
    }
    if (modal.data?.documents?.find((doc) => doc.type === toUpper("payment_tracking"))) {
      checkPaymentTracking = true;
    }
    setDownloadButtonActive({ receipt: checkReceipt, payment_tracking: checkPaymentTracking });
  }, [modal.data?.documents]);

  const { t } = useTranslation();
  const downloadFile = async (docId, type) => {
    setDownloadLoading({ ...downloadLoading, [type]: true });
    const file = await ExpenseRefund.downloadDocument(docId);
    if (file) {
      const parts = split(modal.data?.documents?.find((doc) => doc.id === docId).filePath, "/");
      const fileName = last(parts);
      const blob = new Blob([file]);
      exportFile(blob, fileName);
      showSuccess();
    } else {
      showError(get(file, "error.messageCode", "ER000"));
    }
    setDownloadLoading({ ...downloadLoading, [type]: false });
  };
  const removeDocument = (file, type) => {
    setDownloadButtonActive({ ...isDownloadButtonActive, [type]: false });
    const index = fileList[type].indexOf(file);
    const newFileList = fileList[type].slice();
    newFileList.splice(index, 1);
    setFileList({ ...fileList, [type]: newFileList });
    if (file.status === "done") {
      // Call API to delete file. I can't use await so i just call it
      ExpenseRefund.deleteDocument(file.uid);
    }
  };
  const dragger = (type) => (
    <Dragger
      disabled={uploading || !canEdit}
      name="file"
      multiple={false}
      onRemove={(file) => removeDocument(file, type)}
      beforeUpload={(file) => {
        if (indexOf(permittedFlatRateUploadFormats, file.type) !== -1) {
          if (file.size <= maxFileFlatRate && file.size !== 0) {
            if (!isEmpty(fileList[type])) {
              removeDocument(fileList[type][0], type);
            }
            setFileList({ ...fileList, [type]: [file] });
            setDownloadButtonActive({ ...isDownloadButtonActive, [type]: false });
          } else {
            if (file.size === 0) {
              showError(t("expenseRefundFolder.uploadFile.errorFileSizeZero"));
            } else {
              showError(t("expenseRefundFolder.uploadFile.errorFileSize"));
            }
          }
        } else {
          showError(t("expenseRefundFolder.uploadFile.errorFileFormat"));
        }
        return false;
      }}
      fileList={fileList[type]}
      accept={permittedFlatRateUploadFormats}
    >
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">{t("expenseRefundFolder.uploadFile.infoTitle")}</p>
      <p className="ant-upload-hint">{t("expenseRefundFolder.uploadFile.infoDesc")}</p>
    </Dragger>
  );

  const getContext = (type) => {
    let contextReturn = t("expenseRefundFolder.uploadFile.notUpload", {
      type: modal.data?.type ? t(`expenseRefundFolder.typologies.${toLower(modal.data?.type)}`) : "",
    });
    switch (modal.data?.type) {
      case "JUSTIFICATION":
        contextReturn = dragger(type);
        if (modal.data?.invoicePfp) {
          contextReturn = (
            <>
              <Row className="mb-10">
                <Col span={24}>
                  <Alert message={t("expenseRefundFolder.justificationInfoBoxUpload")} type="warning" />
                </Col>
              </Row>
              {dragger(type)}
            </>
          );
        }
        break;
      case "FLAT_RATE":
        if (modal.data?.flight) {
          contextReturn = dragger(type);
        } else {
          contextReturn = t("expenseRefundFolder.uploadFile.notUploadLessFlight", {
            type: modal.data?.type ? t(`expenseRefundFolder.typologies.${toLower(modal.data?.type)}`) : "",
          });
        }
        break;
      case "CREDIT_CARD":
        contextReturn = dragger(type);
        break;
    }
    return (
      <div>
        <Row className="mb-10">
          <h3>{t(`expenseRefundFolder.typeTraceablePayment.${type}`)}</h3>
        </Row>
        <Row className="mb-10">
          <Button
            isLoading={downloadLoading[type]}
            content={t("general.downloadAttachment")}
            isDisabled={!isDownloadButtonActive[type]}
            clickCallback={() =>
              downloadFile(modal.data?.documents?.find((doc) => doc.type === toUpper(type)).id, type)
            }
            currentIcon={<DownloadOutlined className="icon-medium" />}
          />
        </Row>
        {contextReturn}
      </div>
    );
  };

  return (
    <>
      <Row className="mb-10" justify="space-around">
        <Col
          lg={modal.data?.traceablePayment ? 10 : 24}
          md={modal.data?.traceablePayment ? 10 : 24}
          xs={24}
          sm={modal.data?.traceablePayment ? 10 : 24}
        >
          {getContext("receipt")}
        </Col>
        {modal.data?.traceablePayment && (
          <Col lg={10} md={10} xs={24} sm={10}>
            {getContext("payment_tracking")}
          </Col>
        )}
      </Row>
    </>
  );
}

export default UploadFile;
